// UPLOAD-PREVIEW-2: the only module that loads pdfjs-dist. Dynamic import keeps
// it out of the main bundle. The worker is the legacy build, same origin.

// .js, not .mjs: serve_frontend only maps .js to a JavaScript content type.
// A .mjs path falls through to index.html and the worker never starts.
export const PDF_WORKER_SRC = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

let loading;

function loadPdfjs() {
  if (!loading) {
    // Literal import() so webpack emits a separate chunk. A variable here
    // is an expression and the library never gets bundled.
    loading = import("pdfjs-dist/legacy/build/pdf.mjs").then((mod) => {
      const pdfjs = mod;
      if (pdfjs.GlobalWorkerOptions) pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
      return pdfjs;
    });
  }
  return loading;
}

async function bytesOf(source) {
  if (source instanceof ArrayBuffer) return new Uint8Array(source.slice(0));
  if (ArrayBuffer.isView(source)) {
    const copy = new Uint8Array(source.byteLength);
    copy.set(new Uint8Array(source.buffer, source.byteOffset, source.byteLength));
    return copy;
  }
  return new Uint8Array(await source.arrayBuffer());
}

async function drawPage(doc, pageNumber, width) {
  const page = await doc.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: (width || 192) / (base.width || 1) });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil(viewport.width));
  canvas.height = Math.max(1, Math.ceil(viewport.height));
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

async function withDocument(source, run) {
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({ data: await bytesOf(source), isEvalSupported: false });
  let doc = null;
  try {
    doc = await task.promise;
    return await run(doc);
  } finally {
    if (doc && doc.destroy) await doc.destroy();
    if (task && task.destroy) await task.destroy();
  }
}

// One open document per lightbox item. Page count and every page reuse it.
const held = new Map();

export async function retainPdf(source) {
  const current = held.get(source);
  if (current) {
    current.refs += 1;
    return current;
  }
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({ data: await bytesOf(source), isEvalSupported: false });
  const doc = await task.promise;
  const entry = { doc, task, refs: 1 };
  held.set(source, entry);
  return entry;
}

export async function releasePdf(source) {
  const entry = held.get(source);
  if (!entry) return;
  entry.refs -= 1;
  if (entry.refs > 0) return;
  held.delete(source);
  if (entry.doc && entry.doc.destroy) await entry.doc.destroy();
  if (entry.task && entry.task.destroy) await entry.task.destroy();
}

export function pdfPageCount(source) {
  const entry = held.get(source);
  if (entry) return Promise.resolve(entry.doc.numPages || 0);
  return withDocument(source, (doc) => doc.numPages || 0);
}

export function renderPage(source, pageNumber, width) {
  const entry = held.get(source);
  if (entry) return drawPage(entry.doc, pageNumber, width);
  return withDocument(source, (doc) => drawPage(doc, pageNumber, width));
}
